"use client";

import { useState, type FormEvent } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { portalFont, portalTokens } from "../partnerTheme";
import { isValidPartnerAccessCode, storeSiteAccessSession } from "../partnerSiteAccess";

const GATE_THEME: Record<
  string,
  { accent: string; blurb: string; eyebrow?: string }
> = {
  skrewball: {
    accent: "#C45C26",
    blurb: "This Skrewball case study is private. Enter the password ComedyUO sent you.",
  },
  "dr-squatch": {
    accent: "#2F7A3E",
    blurb: "This Dr. Squatch portal is private. Enter the password ComedyUO sent you.",
  },
  "draft-kings": {
    accent: "#72BB3B",
    eyebrow: "Case study",
    blurb:
      "Private ComedyUO case study of The Taping, using our Dr. Squatch partnership as the example. Enter the password ComedyUO sent you.",
  },
  "amex-esb": {
    accent: "#006FCF",
    eyebrow: "American Express overview",
    blurb:
      "Private partnership concept for the Empire State Residency. Enter the password ComedyUO sent you.",
  },
  "bilt-esb": {
    accent: "#E8E8E8",
    eyebrow: "BILT overview",
    blurb:
      "Private partnership concept for the Empire State Residency. Enter the password ComedyUO sent you.",
  },
  "delta-esb": {
    accent: "#E8E8E8",
    eyebrow: "Delta overview",
    blurb:
      "Private partnership concept for the Empire State Residency. Enter the password ComedyUO sent you.",
  },
  "united-esb": {
    accent: "#E8E8E8",
    eyebrow: "United overview",
    blurb:
      "Private partnership concept for the Empire State Residency. Enter the password ComedyUO sent you.",
  },
  "capital-one-esb": {
    accent: "#004977",
    eyebrow: "Partnership overview for Capital One",
    blurb: "Enter the password ComedyUO sent you.",
  },
  "draft-kings-stadium": {
    accent: "#72BB3B",
    eyebrow: "ComedyUO overview",
    blurb:
      "An overview of ComedyUO, our Stadium Tour offering, and our comedy tapings. Enter the password ComedyUO sent you.",
  },
  "food-tour": {
    accent: "#D4A574",
    eyebrow: "Food Tour overview",
    blurb:
      "Private partnership concept for the ComedyUO Food Tour. Enter the password ComedyUO sent you.",
  },
  "hexclad-food-tour": {
    accent: "#E8672D",
    eyebrow: "HexClad Food Tour",
    blurb:
      "Private partnership concept for HexClad and the ComedyUO Food Tour. Enter the password ComedyUO sent you.",
  },
  gti: {
    accent: "#1F7A3A",
    eyebrow: "Green Thumb Industries",
    blurb: "Private brand overview for GTI. Enter the password ComedyUO sent you.",
  },
  airbnb: {
    accent: "#FF5A5F",
    eyebrow: "Airbnb overview",
    blurb: "Private partnership concept for Airbnb. Enter the password ComedyUO sent you.",
  },
};

const DEFAULT_THEME = {
  accent: "#2F7A3E",
  blurb: "This partner portal is private. Enter the password ComedyUO sent you.",
};

export default function PartnerPassphraseGate({
  slug,
  onVerified,
}: {
  slug: string;
  onVerified: () => void;
}) {
  const theme = GATE_THEME[slug] ?? DEFAULT_THEME;
  const buttonTextColor =
    slug === "draft-kings" ||
    slug === "draft-kings-stadium" ||
    slug === "bilt-esb" ||
    slug === "delta-esb" ||
    slug === "united-esb" ||
    slug === "food-tour" ||
    slug === "hexclad-food-tour"
      ? "#0a0a0a"
      : "#fff";
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    if (!isValidPartnerAccessCode(slug, code)) {
      setSubmitting(false);
      setError("That password is not correct.");
      return;
    }
    storeSiteAccessSession(slug);
    setSubmitting(false);
    onVerified();
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: portalTokens.background,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2.5,
        py: 8,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, md: 4 },
          borderRadius: "24px",
          border: `1px solid color-mix(in srgb, ${theme.accent} 35%, transparent)`,
          bgcolor: "rgba(18,20,23,0.92)",
        }}
      >
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: theme.accent,
            mb: 1.5,
          }}
        >
          {theme.eyebrow || "Partner portal"}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.6rem", md: "1.85rem" },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            mb: 1.25,
          }}
        >
          Enter the password
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            color: "rgba(244,245,247,0.72)",
            fontSize: "0.95rem",
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          {theme.blurb}
        </Typography>
        <TextField
          fullWidth
          required
          autoComplete="off"
          type="password"
          label="Password"
          placeholder="Password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.04)",
              alignItems: "center",
              minHeight: 56,
            },
            "& .MuiInputBase-input": {
              py: 1.75,
              lineHeight: 1.4,
              letterSpacing: "0.06em",
            },
            "& .MuiInputLabel-root": { color: "rgba(244,245,247,0.65)" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `color-mix(in srgb, ${theme.accent} 45%, transparent)`,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${theme.accent} !important`,
            },
            "& .MuiInputLabel-root.Mui-focused": { color: theme.accent },
          }}
        />
        {error ? (
          <Typography
            sx={{
              fontFamily: portalFont,
              color: "#f87171",
              fontSize: "0.875rem",
              mb: 2,
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          type="submit"
          disabled={submitting || !code.trim()}
          fullWidth
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: theme.accent,
            color: buttonTextColor,
            borderRadius: "999px",
            py: 1.4,
            fontSize: "0.975rem",
            "&:hover": { bgcolor: theme.accent, filter: "brightness(1.1)" },
            "&.Mui-disabled": {
              color:
                slug === "draft-kings" ||
                slug === "draft-kings-stadium" ||
                slug === "bilt-esb" ||
                slug === "delta-esb" ||
                slug === "united-esb" ||
                slug === "food-tour" ||
                slug === "hexclad-food-tour"
                  ? "rgba(10,10,10,0.45)"
                  : "rgba(255,255,255,0.5)",
            },
          }}
        >
          {submitting ? (
            <CircularProgress size={22} sx={{ color: buttonTextColor }} />
          ) : (
            "Continue"
          )}
        </Button>
      </Box>
    </Box>
  );
}
